import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Field } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

declare global {
  interface Window {
    turnstile?: { render(el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void; theme?: string }): string; reset(id?: string): void };
  }
}

const KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || '1x00000000000000000000AA';

type Phase = 'creds' | '2fa' | 'enrol';

export default function LoginPage() {
  const { me, reload } = useAuth();
  const nav = useNavigate();
  const [phase, setPhase] = useState<Phase>('creds');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tsToken, setTsToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const tsRef = useRef<HTMLDivElement>(null);
  const [pendingUserId, setPendingUserId] = useState('');
  const [twofaCode, setTwofaCode] = useState('');

  useEffect(() => {
    if (me) nav('/', { replace: true });
  }, [me, nav]);

  useEffect(() => {
    if (phase !== 'creds') return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    document.body.appendChild(s);
    const t = setInterval(() => {
      if (window.turnstile && tsRef.current && !tsRef.current.dataset.rendered) {
        tsRef.current.dataset.rendered = '1';
        window.turnstile.render(tsRef.current, { sitekey: KEY, theme: 'auto', callback: (tok) => setTsToken(tok) });
        clearInterval(t);
      }
    }, 200);
    return () => clearInterval(t);
  }, [phase]);

  const submitCreds = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await api<{ ok?: boolean; needs2fa?: boolean; userId?: string; mustEnrol?: boolean }>(
        '/api/auth/login',
        { body: { email, password, turnstileToken: tsToken } },
      );
      if (r.needs2fa && r.userId) {
        setPendingUserId(r.userId);
        setPhase('2fa');
        setLoading(false);
        return;
      }
      await reload();
      nav('/', { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Error');
      window.turnstile?.reset();
      setTsToken('');
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api('/api/auth/2fa/verify', { body: { userId: pendingUserId, code: twofaCode } });
      await reload();
      nav('/', { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  if (phase === 'enrol') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <div style={{ maxWidth: 480, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, textAlign: 'center' }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent)' }}>SEGURIDAD REQUERIDA</div>
          <h1 className="display" style={{ fontSize: 24, fontWeight: 600, margin: '12px 0' }}>Activá 2FA para entrar</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.6 }}>
            Las cuentas de administrador y operador exigen autenticación en dos pasos. Vamos a configurarlo ahora con una app como Google Authenticator, 1Password o Authy.
          </p>
          <Btn onClick={() => nav('/2fa-setup', { state: { pendingUserId, email } })}>Configurar 2FA</Btn>
        </div>
      </div>
    );
  }

  if (phase === '2fa') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <form onSubmit={submit2fa} style={{ width: '100%', maxWidth: 380, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent) ' }}>2FA</div>
          <h1 className="display" style={{ fontSize: 24, fontWeight: 600, margin: '8px 0 16px' }}>Código de tu app</h1>
          <input
            value={twofaCode}
            onChange={(e) => setTwofaCode(e.target.value)}
            autoFocus
            placeholder="123456 ó aBcd-1234"
            className="mono"
            style={{ width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border-2)', color: 'var(--text)', padding: '14px 16px', borderRadius: 10, fontSize: 22, fontWeight: 600, letterSpacing: '0.15em', outline: 'none', fontFamily: 'JetBrains Mono', textAlign: 'center' }}
          />
          {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 10 }}>{error}</div>}
          <Btn type="submit" disabled={loading || twofaCode.length < 6}>
            {loading ? 'Validando…' : 'Verificar'}
          </Btn>
          <p style={{ marginTop: 12, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
            Si perdiste el acceso, usa un código de respaldo de un solo uso.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <form onSubmit={submitCreds} style={{ width: '100%', maxWidth: 380, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent)' }}>BACKOFFICE · TUCUPET</div>
        <h1 className="display" style={{ fontSize: 28, fontWeight: 600, margin: '8px 0 20px' }}>Iniciá sesión</h1>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Correo" type="email" value={email} onChange={setEmail} />
          <Field label="Contraseña" type="password" value={password} onChange={setPassword} />
          <div ref={tsRef} />
          {error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
          <Btn type="submit" disabled={loading || !tsToken}>
            {loading ? 'Ingresando...' : 'Entrar'}
          </Btn>
        </div>
      </form>
    </div>
  );
}
